import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Container, LoadingState, PageHeader, Text } from '@bn-apps/dim-ui-react';
import { useRailMLFiles } from '@hooks/useSparql';
import { createCoverageLayer } from '@utils/geojson.utils';
import MapView from './MapView';
import TooltipOverlay from './TooltipOverlay';
import type { GeoJSONFeatureCollection, GeoJSONFeature, FeatureProperties } from '@/types';
import appStyles from '@/App.module.scss';

/**
 * MapPage component - Geographic map visualization of railway network
 * User Story 5: Interactive map with railML file coverage areas
 */
export default function MapPage(): JSX.Element {
  const { t } = useTranslation();
  const { data: railmlFiles, loading: filesLoading, error: filesError } = useRailMLFiles();

  const [coverageData, setCoverageData] = useState<GeoJSONFeatureCollection>({
    type: 'FeatureCollection' as const,
    features: []
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [hoveredFeature, setHoveredFeature] = useState<FeatureProperties | null>(null);
  const [hoveredPosition, setHoveredPosition] = useState<{ lng: number; lat: number } | null>(null);
  const [highlightedFeatureId, setHighlightedFeatureId] = useState<string | null>(null);
  const [filterActive, setFilterActive] = useState<boolean>(false);

  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  // Load map data when files are loaded
  useEffect(() => {
    if (filesLoading) {
      setLoading(true);
      return;
    }

    if (filesError) {
      setError(filesError);
      setLoading(false);
      return;
    }

    // Create GeoJSON coverage layer from loaded data
    // Note: For now, operational points and lines would need separate queries
    const coverage = createCoverageLayer(
      [],
      [],
      railmlFiles || []
    );
    setCoverageData(coverage as GeoJSONFeatureCollection);
    setLoading(false);
  }, [filesLoading, filesError, railmlFiles]);

  // Handle feature click
  const handleFeatureClick = (feature: FeatureProperties | null): void => {
    if (!feature || filterActive) return;

    // If feature has associated files, trigger download of first file
    if (feature.railmlFiles && feature.railmlFiles.length > 0) {
      const file = feature.railmlFiles[0];
      console.log('Downloading file:', file.filename);
      // TODO: Trigger file download
      // This would integrate with files.service.js downloadFile()
    }

    setHighlightedFeatureId(feature.id);
  };

  // Handle feature hover
  const handleFeatureHover = (feature: FeatureProperties | null, position: { lng: number; lat: number } | null): void => {
    setHoveredFeature(feature);
    setHoveredPosition(position);
  };

  if (loading) {
    return (
      <Container className={appStyles.pageContent}>
        <div className="bn-flex bn-flex-col bn-items-center bn-justify-center bn-p-600 bn-bg-surface bn-rounded-300">
          <LoadingState label={t('map.loading')} />
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className={appStyles.pageContent}>
        <div className="bn-flex bn-flex-col bn-items-center bn-justify-center bn-p-600 bn-bg-surface bn-rounded-300">
          <Text className="bn-text-error bn-mbe-400">{error}</Text>
          <Button onClick={() => window.location.reload()} variant="primary" size="md">
            {t('common.retry')}
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className={appStyles.pageContent}>
      <div className="bn-mbe-500">
        <PageHeader title={t('map.title')} lead={t('map.description')} />
        {filterActive && (
          <div className="bn-flex bn-items-center bn-gap-300 bn-mbs-400 bn-p-300 bn-bg-warning-light bn-rounded-200">
            <span aria-hidden="true">⚠️</span>
            <span>{t('map.filterActiveNotice')}</span>
            <Button
              onClick={() => setFilterActive(false)}
              variant="tertiary"
              size="sm"
            >
              {t('map.clearFilter')}
            </Button>
          </div>
        )}
      </div>

      <div ref={mapContainerRef} className="bn-mbe-500" style={{ height: '500px', position: 'relative' }}>
        <MapView
          coverageData={coverageData}
          highlightedFeatureId={highlightedFeatureId}
          onFeatureClick={handleFeatureClick}
          onFeatureHover={handleFeatureHover}
          interactionEnabled={!filterActive}
        />
        <TooltipOverlay
          featureProperties={hoveredFeature}
          position={hoveredPosition}
          mapContainer={mapContainerRef.current}
        />
      </div>

      <div className="bn-bg-surface bn-p-400 bn-rounded-300 bn-border bn-border-stroke bn-mbe-500">
        <h3 className="bn-text-lg bn-font-semibold bn-mbe-300">{t('map.legend.title')}</h3>
        <div className="bn-flex bn-flex-wrap bn-gap-400">
          <div className="bn-flex bn-items-center bn-gap-200">
            <span className="bn-inline-block bn-w-4 bn-h-4 bn-rounded-full bn-bg-primary" />
            <span className="bn-text-sm">{t('map.legend.station')}</span>
          </div>
          <div className="bn-flex bn-items-center bn-gap-200">
            <span className="bn-inline-block bn-w-4 bn-h-4 bn-rounded-full bn-bg-secondary" />
            <span className="bn-text-sm">{t('map.legend.stopPost')}</span>
          </div>
          <div className="bn-flex bn-items-center bn-gap-200">
            <span className="bn-inline-block bn-w-4 bn-h-4 bn-rounded-full bn-bg-tertiary" />
            <span className="bn-text-sm">{t('map.legend.other')}</span>
          </div>
          <div className="bn-flex bn-items-center bn-gap-200">
            <span className="bn-inline-block bn-w-4 bn-h-2 bn-bg-brand-primary" />
            <span className="bn-text-sm">{t('map.legend.line')}</span>
          </div>
        </div>
      </div>

      {/* Text-based alternative for accessibility (ACC-008) */}
      <div className="bn-bg-surface bn-p-500 bn-rounded-300 bn-border bn-border-stroke">
        <h2 className="bn-text-xl bn-font-semibold bn-mbe-300">{t('map.textAlternative.title')}</h2>
        <p className="bn-text-base bn-mbe-400">{t('map.textAlternative.description')}</p>
        <div className="bn-overflow-x-auto">
          <table className="bn-w-full">
            <thead className="bn-bg-surface-secondary">
              <tr>
                <th className="bn-p-300 bn-text-left bn-font-semibold">{t('map.textAlternative.label')}</th>
                <th className="bn-p-300 bn-text-left bn-font-semibold">{t('map.textAlternative.type')}</th>
                <th className="bn-p-300 bn-text-left bn-font-semibold">{t('map.textAlternative.fileCount')}</th>
              </tr>
            </thead>
            <tbody>
              {coverageData.features.map((feature: GeoJSONFeature) => (
                <tr key={feature.properties.id} className="bn-border-b bn-border-stroke">
                  <td className="bn-p-300">{feature.properties.label}</td>
                  <td className="bn-p-300">
                    {t(
                      `map.textAlternative.${feature.properties.type}`
                    )}
                  </td>
                  <td className="bn-p-300">{feature.properties.fileCount || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Container>
  );
}
